// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2023-09-06 23:31:04 +0200 (Wed, 06 Sep 2023) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 136729 $
//  Purpose     : logging functions
// =============================================================================
// ---Third-party imports
import debug, {enable} from 'debug'
import {createLogger} from 'redux-logger'
import {createAction, handleAction} from 'redux-actions'
// ---Framework imports
import {keys, contains, get, iterItems, dict, size} from 'ldash'
//~ import createSagaMonitor from './sagamon'
import {titleSuffix, titleFormatter, logFilter, ACTIVE_ACTIONS} from './formatter'
import {persistReducer} from 'uix/persist'
import {handleSubState} from 'uix/utils/reducers'
import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/autoMergeLevel1'

// ---Globals
// enable diffs in redux-logger (make things slow)
let ACTION_DIFF = false
//~ ACTION_DIFF = true

// will trigger reportError
//~ let reportException = createAction('REPORT_EXCEPTION', args => ({args}))
// will be tracked by watchLoggedExceptions saga
const loggedExceptions = []
//~ let BROWSER_STORE = null

// needs to be done manually in webpack/nodemon
enable(process.env.DEBUG)

const colors = {
  // [ansi_value, css_value]
  RED: [1, '#FF0000'],
  GREEN: [2, '#22CC22'],
  ORANGE: [3, '#FFCC00'],
  BLUE: [4, '#2222CC'],
  MAGENTA: [5, '#8B008B'],
  CYAN: [6, '#008B8B'],
  BLACK: [7, '#000000'],
}
const channels = {
  // first two will be rejected on-svn-commit
  uix: colors.BLACK, // temporary ones: log('my-message: %o', o)
  uix_x: colors.CYAN, // colorful temporary ones log.x('my-message: %o', o)
  uix_debug: [0, '#CCCCCC'],
  uix_info: colors.GREEN,
  uix_warn: colors.ORANGE,
  uix_error: colors.RED,
  uix_exception: colors.RED,
  uix_action: colors.MAGENTA, // only used in ssr action dispatching
  uix_saga: colors.CYAN,
  uix_api: colors.BLUE,
  uix_ssr: colors.ORANGE,
  uix_chunk: colors.BLUE,
  uix_startup: colors.MAGENTA,
}

// no initial logging before persist reconciler runs (see below)
const INITIAL_ACTIVE = {startup: false}

const channel2log = {}

let logApi = null
let maxLength = Math.max(...keys(channels).map(n => n.length))

let activate = active => {
  /* replace logApi channel funcs with null emitters if not active
   */
  iterItems(active, (name, f) => {
    let is_active = active[name] || active[name] === undefined
    if (name.startsWith('action/')) {
      ACTIVE_ACTIONS[name.slice(7)] = is_active
    } else if (channel2printer[name]) {
      if (is_active) {
        logApi[name] = channel2printer[name]
      } else {
        logApi[name] = () => null
      }
    }
  })
}

let setup = () => {
  /* setup log channels
   */
  let channel2printer = {}
  iterItems(channels, (name, cfg) => {
    let lg = null

    if (!name.includes('_')) {
      // the raw log() channel
      lg = debug(name.padEnd(maxLength))
      logApi = lg
    } else {
      // all other channels
      lg = debug(name.replace('_', ':').padEnd(maxLength))
      let n = name.slice(4)

      if (name === 'uix_exception') {
        logApi[n] = (...args) => {
          // additionally print raw to console
          // chrome recovers more sourcemapped line numbers
          args.forEach(e => e instanceof Error && console.error(e))
          lg(...args)
          loggedExceptions.push(args)
          //~ if (BROWSER_STORE) {
          //~ BROWSER_STORE.dispatch(reportException(args))
          //~ }
        }
      } else {
        logApi[n] = lg
      }

      channel2printer[n] = lg
    }

    if (!process.env.DEBUG_COLORS) {
      // formatting failures in mode = production
      lg.useColors = false
    }

    channel2log[name] = lg

    if (process.env.UIX_ENV.includes('browser')) {
      // use css values
      lg.color = cfg[1]
    } else {
      // use ansi values
      lg.color = cfg[0]
    }
  })

  return {channel2printer}
}
const {channel2printer} = setup()

// initial state before restoring last state with persist
activate(INITIAL_ACTIVE)

const bufferedLogLines = []

let resetLogTimings = () => {
  //~   each([...debug.instances], (lg) => {
  //~     if (lg.namespace == 'uix'.padEnd(maxLength)) {
  //~       each(entries(lg), ([name, value]) => {
  //~         if (value.namespace) {
  //~           value.prev = undefined;
  //~           value.curr = undefined;
  //~           value.diff = undefined
  //~         }
  //~       })
  //~     }
  //~     lg.prev = undefined;
  //~     lg.curr = undefined;
  //~     lg.diff = undefined;
  //~     log(lg)
  //~     lg.destroy()
  //~   })
  //~   setup()
}

// capture?

if (process.env.UIX_LOGS) {
  debug.log = (msg, duration, ...args) => {
    console.log(msg, duration, ...args)
    // console.log(args)
    if (msg.endsWith('%s')) {
      msg = msg.slice(0, -2)
      // XXX: Hmm, where is duration?
      //~ console.log('XXX', duration)
      //~ msg += `${duration}`
    }
    bufferedLogLines.push(msg)
  }
}

let createServerLogger = () => {
  /* log to ssr console
   */
  let predicate = logFilter

  return store => next => action => {
    const {getState} = store

    if (predicate && !predicate(getState, action)) {
      return next(action)
    }

    if (typeof console === 'undefined') {
      return next(action)
    }

    const returnValue = next(action)
    //~ const time = new Date()

    const actionTitleSuffix = titleSuffix(action)

    logApi.action(`${action.type}${actionTitleSuffix}`)

    return returnValue
  }
}

let createBrowserDevLogger = () =>
  /* log redux actions
   */
  createLogger({
    titleFormatter,
    predicate: logFilter,
    collapsed: true,
    diff: ACTION_DIFF,
  })

let createBrowserProdLogger = () => {
  /* send log.exceptions calls to server
   */
  return store => {
    //~ BROWSER_STORE = store // ok, this is a hack
    return next => action => {
      return next(action)
    }
  }
}

let actions = {
  setActiveLog: createAction('SET_ACTIVE_LOG', (active = {}) => ({active})),
}

let reducers = {
  meta: [
    handleSubState('log', [
      persistReducer(
        handleAction(
          actions.setActiveLog,
          ({active, ...log}, {payload}) => {
            let nextActive = {...active, ...payload.active}
            activate(nextActive)
            return {...log, active: nextActive}
          },
          {active: INITIAL_ACTIVE} // key: channel name, value: is_active
        ),
        {
          key: 'uix_log',
          whitelist: ['active'],
          stateReconciler: (inboundState, ...args) => {
            logApi.info('configure %o log channels', size(inboundState.active))
            // synchronize after un-persist
            activate(inboundState.active)
            return autoMergeLevel1(inboundState, ...args)
          },
        }
      ),
    ]),
  ],
}

export {
  logApi as default,
  logApi as log,
  channels,
  logFilter,
  createServerLogger,
  bufferedLogLines,
  loggedExceptions,
  resetLogTimings,
  createBrowserDevLogger,
  createBrowserProdLogger,
  //~ createSagaMonitor,
  actions,
  reducers,
}
